import { useState } from 'react';

const steps = [
  {
    number: '01',
    tag: 'For admins',
    title: 'Create your organisation and invite members.',
    description:
      'Sign up, name your organisation, and build your team structure in minutes — departments, units, sub-teams. Then invite members by link or email. They join, get assigned to their group, and are ready to go.',
    bullets: [
      'Unlimited groups, departments and sub-teams',
      'Invite by link or import a member list',
      'Assign roles: admin, leader, scanner, member',
    ],
    image: 'https://res.cloudinary.com/damiraph/image/upload/v1765886420/Feature3_hwp6ip.png',
    imageAlt: 'Manage your organisation in the Inked admin dashboard',
  },
  {
    number: '02',
    tag: 'For members',
    title: 'Download the app. Scan in. Done.',
    description:
      'Members download Inked from the App Store or Google Play. They get a personal QR code that belongs to them. At every event, they scan in at the door — or a leader scans their code. Attendance is marked in under 3 seconds.',
    bullets: [
      'Personal QR code on every member profile',
      'Scan in or be scanned — both work',
      'Virtual check-in for online events too',
    ],
    image: 'https://res.cloudinary.com/damiraph/image/upload/v1765886421/Feature1_docefz.png',
    imageAlt: 'Member QR code check-in on the Inked mobile app',
  },
  {
    number: '03',
    tag: 'For admins',
    title: 'Watch reports update in real time.',
    description:
      'Your dashboard updates the moment a member checks in. See total attendance, attendance rate per event, attendance by unit, and member growth — all in one place. Export to Excel or PDF whenever you need it.',
    bullets: [
      'Live attendance trend charts',
      'Attendance by event, member and unit',
      'Export as Excel or PDF in one click',
    ],
    image: 'https://res.cloudinary.com/damiraph/image/upload/v1765886420/Feature2_mq53br.png',
    imageAlt: 'Real-time attendance reports in the Inked dashboard',
  },
];

export default function HowItWorks() {
  const [active, setActive] = useState(0);
  const step = steps[active];

  return (
    <section id="how-it-works" className="bg-white py-24 px-6 lg:px-8 border-t border-gray-100">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-16">
          <p
            className="text-xs font-semibold text-[#2228B6] uppercase tracking-widest mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            How it works
          </p>
          <h2
            className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Up and running in minutes.
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Three steps from sign-up to your first attendance report.
          </p>
        </div>

        {/* Step tabs */}
        <div className="flex gap-2 mb-10 justify-center">
          {steps.map((s, i) => (
            <button
              key={s.number}
              onClick={() => setActive(i)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                active === i
                  ? 'bg-[#2228B6] text-white'
                  : 'border border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300'
              }`}
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <span className="text-xs font-bold">{s.number}</span>
              <span className="hidden sm:inline">{s.tag}</span>
            </button>
          ))}
        </div>

        {/* Active step content */}
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Text side */}
          <div className="space-y-6 order-2 md:order-1">
            <div>
              <span
                className="inline-block text-xs font-semibold text-[#2228B6] bg-[#2228B6]/08 border border-[#2228B6]/15 px-3 py-1 rounded-full tracking-wide uppercase mb-4"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Step {step.number}
              </span>
              <h3
                className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {step.title}
              </h3>
            </div>

            <p
              className="text-base text-gray-500 leading-relaxed"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {step.description}
            </p>

            <ul className="space-y-3">
              {step.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-1 w-4 h-4 rounded-full bg-[#2228B6]/10 border border-[#2228B6]/25 flex items-center justify-center flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2228B6]" />
                  </span>
                  <span
                    className="text-sm text-gray-600"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {b}
                  </span>
                </li>
              ))}
            </ul>

            {/* Step navigation */}
            <div className="flex items-center gap-4 pt-2">
              <button
                onClick={() => setActive((a) => Math.max(0, a - 1))}
                disabled={active === 0}
                className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                ←
              </button>
              <div className="flex gap-1.5">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      i === active ? 'w-6 bg-[#2228B6]' : 'w-1 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={() => setActive((a) => Math.min(steps.length - 1, a + 1))}
                disabled={active === steps.length - 1}
                className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                →
              </button>
            </div>
          </div>

          {/* Image side */}
          <div className="order-1 md:order-2">
            <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 shadow-sm">
              {/* Browser chrome bar */}
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-gray-200 bg-white">
                <span className="w-2.5 h-2.5 rounded-full bg-gray-200" />
                <span className="w-2.5 h-2.5 rounded-full bg-gray-200" />
                <span className="w-2.5 h-2.5 rounded-full bg-gray-200" />
                <span className="ml-3 text-xs text-gray-400 font-mono" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {active === 0 ? 'app.inked.fit/structure' : active === 1 ? 'Inked — Mobile App' : 'app.inked.fit/reports'}
                </span>
              </div>
              <img
                key={step.image}
                src={step.image}
                alt={step.imageAlt}
                className="w-full h-auto block"
              />
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
